/*
 * Prove7 Protect — governed markdown renderer for the Run Center.
 *
 * Agent output is the Protected Twin's own C(k+1): it has already crossed the
 * boundary — DPG scanned and redacted it, the policy gate ruled on it, the
 * envelope sealed it. What the twin does NOT do, because it is not what it is
 * for, is neutralise markup: DPG classifies DATA ("must this leave?"), not
 * markup ("is this safe to interpret?"). So this renderer never inserts source
 * text as HTML. Every leaf string is escaped at emit time and the only markup in
 * the output is the markup this file writes — safe by construction rather than
 * by trust or by a blocklist, which is the same discipline either way.
 *
 * It also renders what the twin leaves behind: [[redacted:CLASS]] markers from a
 * governed turn become a redaction pill, so a masked value reads as masked
 * rather than as the literal marker text.
 *
 * Supported: headings, paragraphs, bold/italic/strike, inline code, fenced code
 * with a language header, links (http/https/mailto/relative only), bullet,
 * numbered and task lists with one nesting level, blockquotes, callouts, tables
 * with per-column alignment and numeric right-alignment, horizontal rules, and
 * Prove7 chips (record ids, incident numbers, policy ids, redaction markers).
 *
 * Deliberately NOT supported (dropped to text): raw HTML, images, iframes,
 * scripts, javascript:/data: URLs.
 *
 * Pure: no DOM access, no network. Returns an HTML string.
 *
 * ── Provenance ───────────────────────────────────────────────────────────────
 * This is the Protect-surface sibling of Control Chat's `P7Markdown`
 * (Chat/web/public/markdown.js), which stays where it is and keeps its own
 * copy. The two are deliberately separate files in separate repositories, so
 * the guard against them drifting apart is the SPEC, not the source: the suite
 * beside this file is Control Chat's own markdown spec, ported case for case.
 * A behavioural change here that is not in that suite is drift; fix both.
 */


  const MAX_TABLE_ROWS = 12;      // beyond this the tail is folded behind "show all"
  const VISIBLE_TABLE_ROWS = 10;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  // Only these URL shapes may become a link. Anything else (javascript:, data:,
  // vbscript:, a bare word) renders as plain text — the label is kept, the href is not.
  function safeHref(url) {
    const u = String(url || '').trim();
    if (/^(https?:\/\/|mailto:)/i.test(u)) return u;
    if (/^\/[^/\\]/.test(u)) return u;            // same-origin absolute path
    return null;
  }

  // ── inline ────────────────────────────────────────────────────────────────
  // Tokenise so that chip promotion and escaping only ever touch leaf text, never
  // markup this file has already emitted.
  const INLINE = /(`[^`\n]+`)|(\*\*[^\n]+?\*\*)|(__[^\n]+?__)|(~~[^\n]+?~~)|(\*[^*\n]+?\*)|(_[^_\n]+?_)|(\[[^\]\n]*\]\([^)\s]*\))/;

  function inline(src) {
    let out = '';
    let rest = String(src == null ? '' : src);
    for (;;) {
      const m = INLINE.exec(rest);
      if (!m) { out += leaf(rest); break; }
      out += leaf(rest.slice(0, m.index));
      const tok = m[0];
      if (tok.startsWith('`')) {
        out += `<code class="md-code">${esc(tok.slice(1, -1))}</code>`;
      } else if (tok.startsWith('**') || tok.startsWith('__')) {
        out += `<strong>${inline(tok.slice(2, -2))}</strong>`;
      } else if (tok.startsWith('~~')) {
        out += `<del>${inline(tok.slice(2, -2))}</del>`;
      } else if (tok.startsWith('[')) {
        const cut = tok.indexOf('](');
        const label = tok.slice(1, cut);
        const href = safeHref(tok.slice(cut + 2, -1));
        out += href
          ? `<a class="md-link" href="${esc(href)}" rel="noopener noreferrer" target="_blank">${inline(label)}</a>`
          : inline(label);
      } else {
        out += `<em>${inline(tok.slice(1, -1))}</em>`;
      }
      rest = rest.slice(m.index + tok.length);
    }
    return out;
  }

  // A leaf string: escape it, then promote the patterns operators care about into chips.
  function leaf(text) {
    if (!text) return '';
    let h = esc(text);
    // Redaction markers emitted by the governed turn.
    h = h.replace(/\[\[redacted:([A-Z]+)\]\]/g,
      (_, c) => `<span class="redact" title="${esc(c)} hidden">redacted</span>`);
    // Salesforce record ids (15 or 18 chars, 00-prefixed object key).
    h = h.replace(/\b(00[0-9A-Za-z]{13}(?:[0-9A-Za-z]{3})?)\b/g,
      (_, id) => chip(id, 'record'));
    // ServiceNow numbers.
    h = h.replace(/\b((?:INC|CHG|PRB|RITM|SIR)\d{7})\b/g, (_, id) => chip(id, 'record'));
    // Policy / rule ids.
    h = h.replace(/\b(r[0-9a-f]{12})\b/g, (_, id) => chip(id, 'policy'));
    // Emoji keep their own size step so they sit on the baseline.
    try {
      h = h.replace(/(\p{Extended_Pictographic})/gu, '<span class="md-emoji">$1</span>');
    } catch (_) { /* older engine without unicode property escapes — skip */ }
    return h;
  }

  function chip(value, kind) {
    return `<span class="md-chip md-chip-${kind}" data-copy="${esc(value)}" title="Click to copy">${esc(value)}</span>`;
  }

  // ── blocks ────────────────────────────────────────────────────────────────
  const CALLOUTS = {
    NOTE: 'note', INFO: 'note', TIP: 'note',
    WARNING: 'warn', CAUTION: 'warn', APPROVAL: 'warn',
    DANGER: 'danger', DENIED: 'danger', BLOCKED: 'danger',
    SUCCESS: 'ok', DONE: 'ok',
  };

  function render(src) {
    const lines = String(src == null ? '' : src).replace(/\r\n?/g, '\n').split('\n');
    const out = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (!line.trim()) { i++; continue; }

      // fenced code
      const fence = /^\s*```\s*([A-Za-z0-9_+-]*)\s*$/.exec(line);
      if (fence) {
        const lang = fence[1] || '';
        const body = [];
        let closed = false;
        i++;
        while (i < lines.length) {
          if (/^\s*```\s*$/.test(lines[i])) { closed = true; i++; break; }
          body.push(lines[i]); i++;
        }
        out.push(codeBlock(lang, body.join('\n'), closed));
        continue;
      }

      // horizontal rule
      if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { out.push('<hr class="md-hr">'); i++; continue; }

      // heading
      const h = /^\s*(#{1,6})\s+(.*)$/.exec(line);
      if (h) {
        const level = Math.min(h[1].length + 2, 6);   // h1 in a message renders as <h3>
        out.push(`<h${level} class="md-h md-h${h[1].length}">${inline(h[2].trim())}</h${level}>`);
        i++; continue;
      }

      // table — a header row followed by a delimiter row
      if (line.includes('|') && i + 1 < lines.length && isDelimiterRow(lines[i + 1])) {
        const block = [];
        while (i < lines.length && lines[i].trim() && lines[i].includes('|')) { block.push(lines[i]); i++; }
        out.push(table(block));
        continue;
      }

      // blockquote / callout
      if (/^\s*>/.test(line)) {
        const body = [];
        while (i < lines.length && /^\s*>/.test(lines[i])) { body.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
        out.push(quote(body));
        continue;
      }

      // list
      if (isListItem(line)) {
        const block = [];
        while (i < lines.length && (isListItem(lines[i]) || /^\s{2,}\S/.test(lines[i]))) { block.push(lines[i]); i++; }
        out.push(list(block));
        continue;
      }

      // paragraph
      const para = [];
      while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i])) { para.push(lines[i].trim()); i++; }
      if (para.length) out.push(`<p class="md-p">${inline(para.join(' '))}</p>`);
      else i++;
    }

    return out.join('');
  }

  function isBlockStart(l) {
    return /^\s*(#{1,6}\s|>|```)/.test(l) || isListItem(l) || /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(l);
  }
  function isListItem(l) { return /^\s*([-*+]\s+|\d+[.)]\s+)/.test(l); }
  function isDelimiterRow(l) { return /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(l) && l.includes('-'); }

  function codeBlock(lang, body, closed) {
    const head = `<div class="md-pre-h"><span class="md-pre-lang">${esc(lang || 'text')}</span>`
      + (closed ? '<button type="button" class="md-copy" data-md-copy>Copy</button>' : '<span class="md-pre-wait">receiving…</span>')
      + '</div>';
    return `<div class="md-pre${closed ? '' : ' md-pre-partial'}">${head}<pre><code>${esc(body)}</code></pre></div>`;
  }

  function quote(bodyLines) {
    let kind = '';
    let lines = bodyLines.slice();
    const marker = /^\s*\[!([A-Z]+)\]\s*(.*)$/.exec(lines[0] || '');
    if (marker && CALLOUTS[marker[1]]) {
      kind = CALLOUTS[marker[1]];
      lines[0] = marker[2];
      if (!lines[0].trim()) lines = lines.slice(1);
    }
    const inner = lines.filter((l) => l.trim()).map((l) => `<p class="md-p">${inline(l.trim())}</p>`).join('');
    return kind
      ? `<div class="md-callout md-callout-${kind}">${inner}</div>`
      : `<blockquote class="md-quote">${inner}</blockquote>`;
  }

  function list(block) {
    // Rows carry their own indent depth; one nesting level is supported (the depth the
    // governed surface actually produces). Deeper indents fold into level 1.
    const rows = [];
    block.forEach((raw) => {
      const m = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/.exec(raw);
      if (m) {
        rows.push({ depth: m[1].length >= 2 ? 1 : 0, ordered: /\d/.test(m[2]), text: m[3] });
      } else if (rows.length) {
        rows[rows.length - 1].text += ' ' + raw.trim();   // lazy continuation line
      }
    });
    if (!rows.length) return '';

    let html = '';
    let open = [];
    const openList = (ordered) => { open.push(ordered); html += ordered ? '<ol class="md-ol">' : '<ul class="md-ul">'; };
    const closeList = () => { html += open.pop() ? '</ol>' : '</ul>'; };

    openList(rows[0].ordered);
    let depth = 0;
    rows.forEach((r) => {
      if (r.depth > depth) { openList(r.ordered); depth = r.depth; }
      else if (r.depth < depth) { closeList(); depth = r.depth; }
      html += item(r.text);
    });
    while (open.length) closeList();
    return html;
  }

  function item(text) {
    const task = /^\[([ xX])\]\s+(.*)$/.exec(text);
    if (task) {
      const done = task[1].toLowerCase() === 'x';
      return `<li class="md-li md-task${done ? ' md-task-done' : ''}">`
        + `<input type="checkbox" class="md-check"${done ? ' checked' : ''} disabled aria-label="${done ? 'Completed' : 'Not completed'}">`
        + `<span>${inline(task[2])}</span></li>`;
    }
    return `<li class="md-li">${inline(text)}</li>`;
  }

  function table(block) {
    const cells = (row) => row.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());
    const header = cells(block[0]);
    const align = cells(block[1]).map((d) => (
      /^:-+:$/.test(d) ? 'center' : /-+:$/.test(d) ? 'right' : /^:-+/.test(d) ? 'left' : ''
    ));
    const body = block.slice(2).map(cells);

    // Alignment is per COLUMN, not per cell: a column auto-right-aligns only when EVERY one of its
    // (non-empty) body cells is numeric — a real numbers column (financial tables). A key/value
    // column that mixes text with one number (e.g. "VTI", "$291.40", a date) stays left-aligned, so
    // a lone number no longer jumps to the right edge while its neighbours sit left. Explicit
    // markdown alignment (:--:, --:, :--) always wins.
    const ncols = body.reduce((m, r) => Math.max(m, r.length), header.length);
    const colAlign = [];
    for (let n = 0; n < ncols; n++) {
      if (align[n]) { colAlign[n] = align[n]; continue; }
      const colCells = body.map((r) => (r[n] != null ? String(r[n]).trim() : '')).filter((c) => c !== '');
      colAlign[n] = (colCells.length > 0 && colCells.every(isNumeric)) ? 'right' : '';
    }

    const th = header.map((c, n) => {
      const a = colAlign[n] ? ` style="text-align:${colAlign[n]}"` : '';
      return `<th${a}>${inline(c)}</th>`;
    }).join('');

    const folded = body.length > MAX_TABLE_ROWS;
    const rowsHtml = body.map((row, idx) => {
      const tds = row.map((c, n) => {
        const a = colAlign[n];
        const cls = isNumeric(c) ? ' class="md-num"' : ''; // tabular figures styling, column decides align
        return `<td${cls}${a ? ` style="text-align:${a}"` : ''}>${inline(c)}</td>`;
      }).join('');
      const hide = folded && idx >= VISIBLE_TABLE_ROWS ? ' class="md-row-extra" hidden' : '';
      return `<tr${hide}>${tds}</tr>`;
    }).join('');

    const more = folded
      ? `<button type="button" class="md-more" data-md-more>Show all ${body.length} rows</button>`
      : '';

    return '<div class="md-tablewrap">'
      + `<table class="md-table"><thead><tr>${th}</tr></thead><tbody>${rowsHtml}</tbody></table>`
      + '</div>' + more;
  }

  function isNumeric(c) {
    return /^[-+]?[$€£]?\s?[\d][\d,]*(\.\d+)?\s?%?$/.test(String(c).trim());
  }

export { render, esc, safeHref };
